import * as am5 from '@amcharts/amcharts5';
import { Grid, Paper, Typography } from '@mui/material';
import i18next from 'i18next';
import { useCallback, useContext, useEffect, useState } from 'react';
import { hidePieSliceLabel } from 'services/common/ChartUtils';
import useStyles from 'style/js-style/containers/dashboard/TabStyle';

import useSelectedAccountId from 'components/common/hooks/useSelectedAccountId';
import { useRef } from 'react';
import { isAbortError } from 'services/common/ApiUtils';
import { ERROR } from 'services/common/Constants';
import { formatNumber } from 'services/common/Utils';
import { Context } from 'states/Store';
import RiskScorePieChart from '../RiskScorePieChart';
import RiskSubScoresPieChart from '../RiskSubScoresPieChart';
import { getSecurityRatingScoresCount } from 'services/edge/SecurityRatingService';
import { getPrefixedSecurityRatingScore, getUnPrefixedSecurityRatingScore, SecurityRatingAllScoresEnum, SecurityRatingGlobalScorePerformanceColor, SecurityRatingGlobalScorePerformanceOrder, SecurityRatingPerformanceColor, SecurityRatingPerformanceOrder } from 'services/common/SecurityRatingUtils';

function TabDashboardSecurityRating() {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [mappedScoreCount, setMappedScoreCount] = useState({});
  const accountId = useSelectedAccountId();

  const abortCtrlRef = useRef();

  const fetchScoreCount = useCallback(
    (query) => {
      if (abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }

      abortCtrlRef.current = new AbortController();
      const signal = abortCtrlRef.current.signal;

      if (accountId && state.account) {
        (async () => {
          try {
            const res = await getSecurityRatingScoresCount(
              accountId,
              query,
              null,
              signal
            );

            let mappedScoresCountsObj = {};
            for (const scoreCounts of res.content) {
              // Selected score from result
              const selectedScore = scoreCounts.score;
              // Selected score counts from result
              const selectedCounts = scoreCounts.scoreCounts;

              // Total tps for this score
              const total = selectedCounts
                ?.map((d) => d.count)
                .reduce((partialSum, a) => partialSum + a, 0);

              let performanceColorObj = SecurityRatingPerformanceColor;
              let performanceOrderObj = SecurityRatingPerformanceOrder;
              if (selectedScore === SecurityRatingAllScoresEnum.GLOBAL_SCORE) {
                performanceColorObj = SecurityRatingGlobalScorePerformanceColor;
                performanceOrderObj = SecurityRatingGlobalScorePerformanceOrder;
              }
              
              mappedScoresCountsObj[selectedScore] = {
                data: selectedCounts
                  .map((countDetail) => ({
                    id: countDetail.scoreLabel,
                    itemLabel: selectedScore !== SecurityRatingAllScoresEnum.GLOBAL_SCORE ? 
                      countDetail.scoreLabel : 
                      i18next.t(`securityRating.maturity.${countDetail.scoreLabel?.toLowerCase()}`),
                    type: getPrefixedSecurityRatingScore(selectedScore),
                    value: countDetail.count,
                    formattedValue: formatNumber(countDetail.count),
                    settings: {
                      fill: am5.color(performanceColorObj[countDetail.scoreValue]),
                    },
                    labelSettings: {
                      forceHidden: hidePieSliceLabel(countDetail.count, total),
                    },
                  }))
                  .sort((a, b) =>
                    performanceOrderObj[a.id] >
                      performanceOrderObj[b.id]
                      ? 1
                      : -1
                  ),
                total: formatNumber(total),
              };
            }
            setMappedScoreCount(mappedScoresCountsObj);
          } catch (e) {
            if (!isAbortError(e)) {
              console.error('error', e);
              dispatch({
                type: 'ALERT',
                alert: { type: ERROR, msg: e.message },
              });
            }
          }
        })();
      }
    },
    [accountId, dispatch, state.account]
  );
  

  // Build filters query from graph filters
  useEffect(() => {
    let queryObj = {};

    if (state.graphFilters?.pie?.length > 0) {
      queryObj = state.graphFilters.pie.reduce((result, item) => {

        const type = getUnPrefixedSecurityRatingScore(item.type);

        if (!result[type]) {
          result[type] = [];
        }
        result[type].push(item.id);
        return result;
      }, {});
    }
    fetchScoreCount(queryObj);

    return () => {
      abortCtrlRef.current?.abort();
    };
  }, [fetchScoreCount, state.graphFilters?.pie]);

  const getSecurityRatingSubScores = () => {
    return Object.keys(mappedScoreCount)
      ?.filter((score) => score !== SecurityRatingAllScoresEnum.GLOBAL_SCORE)
      .map((score) => ({
        scoreLabel: i18next.t(`securityRating.scores.${score}`),
        data: mappedScoreCount?.[score],
      }));
  };

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <Grid container spacing={2} style={{ height: '100%' }}>
        <Grid item xs={4} lg={4} key="security-rating-global">
          <Paper className={classes.graphPaper}>
            <Typography variant="h3" className={classes.graphAreaTitle}>
              <div style={{ display: 'flex' }}>
                <span>{i18next.t(`securityRating.dashboard.title`)}</span>
              </div>
            </Typography>
            <RiskScorePieChart
              subTab={'securityRating'}
              scoreCount={mappedScoreCount?.[SecurityRatingAllScoresEnum.GLOBAL_SCORE]}
            />
          </Paper>
        </Grid>
        <Grid item xs={8} lg={8} key="security-rating-subscores">
          <RiskSubScoresPieChart
            title={i18next.t(`securityRating.dashboard.subScoresTitle`)}
            riskLabel="securityRating"
            subScores={getSecurityRatingSubScores()}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default TabDashboardSecurityRating;
